import React, {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { Button, Dropdown, Form, Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faReply,
  faQuoteLeft,
  faPen,
  faTrash,
  faLock,
  faEllipsis,
  faFaceSmile,
  faRotateLeft,
  faArrowUp,
} from '@fortawesome/free-solid-svg-icons';
import { ClipLoader } from 'react-spinners';
import AppContext from '../context/AppContext';
import CommentsAPI from '../api/CommentsAPI';
import { CommentModel, CommentStatus } from '../types/CommentModel';
import Avatar from './Avatar';

// Emoji offered in the reaction picker (Google-chat style quick set).
const REACTION_CHOICES = ['👍', '❤️', '😂', '🙏', '😮', '🎉'];

// A reply/quote/compose target. `parentId === null` = a new top-level comment.
export type ComposeTarget = {
  parentId: number | null;
  quotedCommentId?: number;
  quotedExcerpt?: string;
  quotedAuthorName?: string;
};

export function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (isNaN(then)) {
    return '';
  }
  const diff = Math.max(0, Date.now() - then);
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

// Notes are private by default. Only a webmaster can publish, by typing #public in the body; the token
// is ignored for everyone else. Mirrors the server's parse for the optimistic copy (see
// CommentsController).
export function isPublicBody(body: string, isWebmaster: boolean): boolean {
  return isWebmaster && /(^|\s)#public\b/i.test(body);
}

// The `#` autocomplete offers only #public, and only to a webmaster. Pattern motifs are tagged with
// TagPicker.
const SUGGESTIONS = ['public'];

// Imperative handle so the Quote toolbar button can drop a blockquote into the main composer.
export type CommentComposerHandle = {
  insertQuote: (text: string) => void;
  focus: () => void;
};

type CommentComposerProps = {
  initialBody?: string;
  submitLabel: string;
  autoFocus?: boolean;
  quotedAuthorName?: string;
  quotedExcerpt?: string;
  onSubmit: (body: string, isPrivate: boolean) => Promise<void>;
  onCancel?: () => void;
};

// Reusable compose/edit box for new comments, replies and edits.
export const CommentComposer = forwardRef<CommentComposerHandle, CommentComposerProps>(
  function CommentComposer(props, ref) {
    const {
      initialBody = '',
      submitLabel,
      autoFocus,
      quotedAuthorName,
      quotedExcerpt,
      onSubmit,
      onCancel,
    } = props;
    // Only a webmaster has a visibility choice.
    const { isWebmaster } = useContext(AppContext);
    const [body, setBody] = useState(initialBody);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    // `#`-autocomplete state. tokenStart is the index of the `#` currently being completed.
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);
    const [menuOpen, setMenuOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [tokenStart, setTokenStart] = useState<number | null>(null);
    const [activeIndex, setActiveIndex] = useState(0);
    const caretToSet = useRef<number | null>(null);

    const suggestions = React.useMemo(() => {
      // Nothing to complete for a normal author.
      if (!menuOpen || !isWebmaster) {
        return [];
      }
      const q = query.toLowerCase();
      const matches = SUGGESTIONS.filter((p) => p.includes(q));
      // Prefix matches first.
      matches.sort((a, b) => {
        const ap = a.startsWith(q) ? 0 : 1;
        const bp = b.startsWith(q) ? 0 : 1;
        return ap - bp || a.localeCompare(b);
      });
      return matches.slice(0, 8);
    }, [menuOpen, query, isWebmaster]);

    // Restore the caret after rewriting the body (inserting a suggestion).
    useLayoutEffect(() => {
      if (caretToSet.current != null && textareaRef.current != null) {
        const pos = caretToSet.current;
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(pos, pos);
        caretToSet.current = null;
      }
    }, [body]);

    // Quote: replace the textbox with the blockquote and put the caret at the end (focus via the effect
    // above).
    useImperativeHandle(ref, () => ({
      insertQuote(text: string) {
        caretToSet.current = text.length;
        setBody(text);
      },
      focus() {
        const el = textareaRef.current;
        if (el == null) {
          return;
        }
        el.focus();
        // Caret at the end, scrolled into view.
        const end = el.value.length;
        el.setSelectionRange(end, end);
      },
    }));

    // Detect a `#word` token immediately left of the caret and drive the picker from it.
    const syncMenu = (value: string, caret: number) => {
      const upto = value.slice(0, caret);
      const m = /#([a-z_]*)$/i.exec(upto);
      if (m) {
        setMenuOpen(true);
        setQuery(m[1]);
        setTokenStart(caret - m[0].length);
        setActiveIndex(0);
      } else {
        setMenuOpen(false);
        setTokenStart(null);
      }
    };

    const onChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const value = e.target.value;
      setBody(value);
      syncMenu(value, e.target.selectionStart ?? value.length);
    };

    const insertSuggestion = (pattern: string) => {
      if (tokenStart == null) {
        return;
      }
      const caret = textareaRef.current?.selectionStart ?? body.length;
      const before = body.slice(0, tokenStart);
      const after = body.slice(caret);
      const inserted = `#${pattern} `;
      const next = before + inserted + after;
      caretToSet.current = before.length + inserted.length;
      setBody(next);
      setMenuOpen(false);
      setTokenStart(null);
    };

    const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (!menuOpen || suggestions.length === 0) {
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex((i) => (i + 1) % suggestions.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex((i) => (i - 1 + suggestions.length) % suggestions.length);
      } else if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        insertSuggestion(suggestions[activeIndex]);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setMenuOpen(false);
      }
    };

    const submit = async () => {
      if (body.trim() === '') {
        return;
      }
      setSaving(true);
      setError('');
      try {
        await onSubmit(body.trim(), !isPublicBody(body, isWebmaster));
        setBody('');
        setMenuOpen(false);
      } catch (e) {
        setError((e as Error).message);
      } finally {
        setSaving(false);
      }
    };

    const isPublic = isPublicBody(body, isWebmaster);

    return (
      <div className="cm-comment-composer">
        {quotedExcerpt != null ? (
          <div className="cm-comment-quote">
            {quotedAuthorName != null ? (
              <span className="cm-comment-quote-author">{quotedAuthorName}</span>
            ) : null}
            <span className="cm-comment-quote-text">{quotedExcerpt}</span>
          </div>
        ) : null}
        <div className="cm-composer-box">
          <div className="cm-composer-input">
            <Form.Control
              as="textarea"
              ref={textareaRef as React.Ref<HTMLTextAreaElement>}
              className="cm-composer-textarea"
              size="sm"
              rows={2}
              autoFocus={autoFocus}
              placeholder="Write a comment..."
              value={body}
              onChange={onChange}
              onKeyDown={onKeyDown}
              onBlur={() => window.setTimeout(() => setMenuOpen(false), 150)}
            />
            {menuOpen && suggestions.length > 0 ? (
              <ul className="cm-pattern-menu" role="listbox">
                {suggestions.map((p, i) => (
                  <li
                    key={p}
                    role="option"
                    aria-selected={i === activeIndex}
                    className={`cm-pattern-item${i === activeIndex ? ' active' : ''}`}
                    // onMouseDown (not onClick) so it fires before the textarea's blur closes the menu.
                    onMouseDown={(e) => {
                      e.preventDefault();
                      insertSuggestion(p);
                    }}
                  >
                    <span className="cm-pattern-hash">#</span>
                    {p}
                    {p === 'private' ? (
                      <span className="cm-pattern-note">makes it private</span>
                    ) : null}
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
          {/* Bottom toolbar, inside the box: rounded send on the right. */}
          <div className="cm-composer-tools">
            <div className="cm-composer-actions">
              {saving ? <ClipLoader size={16} color="#888" /> : null}
              {onCancel != null ? (
                <button
                  type="button"
                  className="cm-composer-cancel"
                  onClick={onCancel}
                  disabled={saving}
                >
                  Cancel
                </button>
              ) : null}
              <button
                type="button"
                className="cm-composer-send"
                onClick={submit}
                disabled={saving || body.trim() === ''}
                title={submitLabel}
                aria-label={submitLabel}
              >
                <FontAwesomeIcon icon={faArrowUp} />
              </button>
            </div>
          </div>
        </div>
        <div className="cm-composer-hint small text-muted mt-1">
          {isWebmaster ? (
            <>
              Only you can see this. Type <code>#public</code> to publish it to everyone
              {isPublic ? <span className="cm-composer-public-flag ms-2">· Public</span> : null}
            </>
          ) : (
            'Only you can see your notes'
          )}
        </div>
        {error ? <div className="text-danger small mt-1">{error}</div> : null}
      </div>
    );
  },
);

// Emoji chips under a comment; clicking toggles your reaction. Read-only when signed out.
function ReactionChips(props: {
  comment: CommentModel;
  canReact: boolean;
  onToggle: (emoji: string) => void;
}): React.ReactNode {
  const { comment, canReact, onToggle } = props;
  if (!comment.Reactions || comment.Reactions.length === 0) {
    return null;
  }
  return (
    <div className="cm-reactions">
      {comment.Reactions.map((r) => (
        <button
          key={r.Emoji}
          type="button"
          className={`cm-reaction-chip${r.Mine ? ' mine' : ''}`}
          disabled={!canReact}
          onClick={() => onToggle(r.Emoji)}
          title={r.Mine ? 'Remove your reaction' : 'React'}
        >
          <span className="cm-reaction-emoji">{r.Emoji}</span>
          <span className="cm-reaction-count">{r.Count}</span>
        </button>
      ))}
    </div>
  );
}

// The hover toolbar: quick-react, reply, edit/delete (own comments), and a "more" menu (quote,
// webmaster moderation).
function CommentToolbar(props: {
  comment: CommentModel;
  canReply: boolean;
  canReact: boolean;
  isWebmaster: boolean;
  onReact: (emoji: string) => void;
  onReply: () => void;
  onQuote: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onModerate: (status: CommentStatus) => void;
  onOpenChange: (open: boolean) => void;
}): React.ReactNode {
  const {
    comment,
    canReply,
    canReact,
    isWebmaster,
    onReact,
    onReply,
    onQuote,
    onEdit,
    onDelete,
    onModerate,
    onOpenChange,
  } = props;
  const isMine = comment.IsMine;
  const removed = comment.Status === 'Removed';
  // Webmaster moderation on someone else's comment (own comments use Delete instead).
  const showRemove = isWebmaster && !isMine && !removed;
  const showRestore = isWebmaster && removed;

  // A self-managed popover rather than a controlled react-bootstrap Dropdown, which never closes when
  // its custom toggle is clicked while open. An outside click closes it too.
  const [reactionOpen, setReactionOpen] = useState(false);
  const reactionWrapRef = useRef<HTMLDivElement | null>(null);
  const setReaction = (open: boolean) => {
    setReactionOpen(open);
    onOpenChange(open);
  };
  useEffect(() => {
    if (!reactionOpen) {
      return;
    }
    const onOutside = (e: Event) => {
      if (reactionWrapRef.current != null && !reactionWrapRef.current.contains(e.target as Node)) {
        setReaction(false);
      }
    };
    document.addEventListener('mousedown', onOutside, true);
    document.addEventListener('touchstart', onOutside, true);
    return () => {
      document.removeEventListener('mousedown', onOutside, true);
      document.removeEventListener('touchstart', onOutside, true);
    };
    // Re-run only when the picker opens or closes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reactionOpen]);

  // Delete (own comment) and moderation live in the overflow menu; everything else is inline.
  const hasOverflow = (isMine && !removed) || showRemove || showRestore;

  return (
    <div className="cm-comment-toolbar">
      {/* Emoji react */}
      {canReact && !removed ? (
        <div className="cm-tb-dd cm-reaction-wrap" ref={reactionWrapRef}>
          <button
            type="button"
            className={`cm-tb-btn${reactionOpen ? ' cm-tb-btn-active' : ''}`}
            title="Add reaction"
            aria-expanded={reactionOpen}
            onClick={() => setReaction(!reactionOpen)}
          >
            <FontAwesomeIcon icon={faFaceSmile} />
          </button>
          {reactionOpen ? (
            <div className="cm-reaction-picker">
              {REACTION_CHOICES.map((emoji) => (
                <button
                  key={emoji}
                  type="button"
                  className="cm-reaction-choice"
                  onClick={() => {
                    onReact(emoji);
                    setReaction(false);
                  }}
                >
                  {emoji}
                </button>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
      {/* Edit */}
      {isMine && !removed ? (
        <button type="button" className="cm-tb-btn" title="Edit" onClick={onEdit}>
          <FontAwesomeIcon icon={faPen} />
        </button>
      ) : null}
      {/* Reply */}
      {canReply && !removed ? (
        <button type="button" className="cm-tb-btn" title="Reply" onClick={onReply}>
          <FontAwesomeIcon icon={faReply} />
        </button>
      ) : null}
      {/* Quote */}
      {canReact && !removed ? (
        <button type="button" className="cm-tb-btn" title="Quote" onClick={onQuote}>
          <FontAwesomeIcon icon={faQuoteLeft} />
        </button>
      ) : null}
      {/* Overflow: delete + moderation */}
      {hasOverflow ? (
        <Dropdown onToggle={onOpenChange} align="end" className="cm-tb-dd">
          <Dropdown.Toggle as="button" bsPrefix="cm-tb-btn" title="More">
            <FontAwesomeIcon icon={faEllipsis} />
          </Dropdown.Toggle>
          <Dropdown.Menu className="cm-tb-menu">
            {isMine && !removed ? (
              <Dropdown.Item className="text-danger" onClick={onDelete}>
                <FontAwesomeIcon icon={faTrash} className="me-2" />
                Delete
              </Dropdown.Item>
            ) : null}
            {showRemove ? (
              <Dropdown.Item className="text-danger" onClick={() => onModerate('Removed')}>
                <FontAwesomeIcon icon={faTrash} className="me-2" />
                Remove (moderation)
              </Dropdown.Item>
            ) : null}
            {showRestore ? (
              <Dropdown.Item onClick={() => onModerate('Visible')}>
                <FontAwesomeIcon icon={faRotateLeft} className="me-2" />
                Restore (moderation)
              </Dropdown.Item>
            ) : null}
          </Dropdown.Menu>
        </Dropdown>
      ) : null}
    </div>
  );
}

export function CommentItem(props: {
  comment: CommentModel;
  isReply: boolean;
  canReply: boolean;
  onReply: (comment: CommentModel) => void;
  onQuote: (comment: CommentModel) => void;
  onEdited: (comment: CommentModel) => void;
  onDeleted: (id: number) => void;
  onModerated: () => void;
}): React.ReactNode {
  const { comment, isReply, canReply, onReply, onQuote, onEdited, onDeleted, onModerated } = props;
  const { isWebmaster, isAuthenticated, isMobile } = useContext(AppContext);
  const [editing, setEditing] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // Keep the toolbar pinned while one of its menus is open.
  const [menuOpen, setMenuOpen] = useState(false);
  // Touch has no hover, so on mobile the ⋯ button reveals the toolbar and a tap elsewhere dismisses it.
  const [revealed, setRevealed] = useState(false);
  const commentRef = useRef<HTMLDivElement | null>(null);

  // Dismiss the revealed toolbar when the next interaction lands outside this comment.
  useEffect(() => {
    if (!revealed) {
      return;
    }
    const onOutside = (e: Event) => {
      if (commentRef.current != null && !commentRef.current.contains(e.target as Node)) {
        setRevealed(false);
      }
    };
    document.addEventListener('touchstart', onOutside, true);
    document.addEventListener('mousedown', onOutside, true);
    return () => {
      document.removeEventListener('touchstart', onOutside, true);
      document.removeEventListener('mousedown', onOutside, true);
    };
  }, [revealed]);

  const removed = comment.Status === 'Removed';
  const canReact = isAuthenticated;

  const del = async () => {
    setDeleting(true);
    try {
      await CommentsAPI.remove(comment.Id);
      onDeleted(comment.Id);
    } finally {
      setDeleting(false);
      setConfirmingDelete(false);
    }
  };

  const moderate = async (status: CommentStatus) => {
    await CommentsAPI.moderate(comment.Id, status);
    onModerated();
  };

  const toggleReaction = async (emoji: string) => {
    const reactions = await CommentsAPI.toggleReaction(comment.Id, emoji);
    onEdited({ ...comment, Reactions: reactions });
  };

  return (
    <div
      ref={commentRef}
      className={`cm-comment${isReply ? ' cm-comment-reply' : ''}${
        menuOpen || revealed ? ' menu-open' : ''
      }`}
    >
      {!editing ? (
        <CommentToolbar
          comment={comment}
          canReply={canReply}
          canReact={canReact}
          isWebmaster={isWebmaster}
          onReact={toggleReaction}
          onReply={() => onReply(comment)}
          onQuote={() => onQuote(comment)}
          onEdit={() => setEditing(true)}
          onDelete={() => setConfirmingDelete(true)}
          onModerate={moderate}
          onOpenChange={setMenuOpen}
        />
      ) : null}

      <div className="cm-comment-head d-flex flex-row align-items-center gap-2">
        <Avatar name={comment.AuthorName} size={26} />
        <div className="cm-comment-head-meta d-flex flex-row flex-wrap align-items-center flex-grow-1">
          <span className="cm-comment-author fw-bold">{comment.AuthorName}</span>
          <span className="cm-comment-time small text-muted">
            {relativeTime(comment.CreatedAt)}
          </span>
          {comment.Visibility === 'Private' ? (
            <span className="small text-muted" title="Private note. Only you can see this">
              <FontAwesomeIcon icon={faLock} className="me-1" />
              Private
            </span>
          ) : null}
        </div>
        {isMobile && !removed && !editing ? (
          <button
            type="button"
            className="cm-comment-more"
            aria-label="Comment actions"
            aria-expanded={revealed}
            onClick={() => setRevealed((v) => !v)}
          >
            <FontAwesomeIcon icon={faEllipsis} />
          </button>
        ) : null}
      </div>

      {removed ? (
        <div className="cm-comment-body text-muted fst-italic">[removed]</div>
      ) : editing ? (
        <CommentComposer
          initialBody={comment.Body}
          submitLabel="Save"
          autoFocus
          onCancel={() => setEditing(false)}
          onSubmit={async (body, isPrivate) => {
            const updated = await CommentsAPI.update(comment.Id, {
              Body: body,
              IsPrivate: isPrivate,
            });
            onEdited(updated);
            setEditing(false);
          }}
        />
      ) : (
        <>
          {comment.QuotedExcerpt != null ? (
            <div className="cm-comment-quote">
              {comment.QuotedAuthorName != null ? (
                <span className="cm-comment-quote-author">{comment.QuotedAuthorName}</span>
              ) : null}
              <span className="cm-comment-quote-text">{comment.QuotedExcerpt}</span>
            </div>
          ) : null}
          <div className="cm-comment-body">{comment.Body}</div>
          <ReactionChips comment={comment} canReact={canReact} onToggle={toggleReaction} />
        </>
      )}

      <Modal
        show={confirmingDelete}
        onHide={() => (deleting ? undefined : setConfirmingDelete(false))}
        centered
        size="sm"
      >
        <Modal.Header closeButton={!deleting}>
          <Modal.Title as="h5">Delete comment?</Modal.Title>
        </Modal.Header>
        <Modal.Body>This can’t be undone.</Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => setConfirmingDelete(false)}
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button variant="danger" onClick={del} disabled={deleting}>
            {deleting ? <ClipLoader size={14} color="#fff" /> : 'Delete'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
